import React, { useState } from 'react';
import { Share, EllipsisVertical, X } from 'lucide-react';
import { usePwaInstall } from '../lib/usePwaInstall';
import { useBottomNavTarget } from '../lib/useBottomNavTarget';

/* Dismissible mobile banner offering to install the PWA. Dismissal (or viewing
   the manual steps) snoozes it for 30 days via usePwaInstall. */
function InstallBanner() {
  const { mode, install, dismiss } = usePwaInstall();
  const [showSteps, setShowSteps] = useState(false);
  const { slug: bottomNavSlug } = useBottomNavTarget();

  if (!mode) return null;

  const closeSteps = () => {
    setShowSteps(false);
    dismiss();
  };

  const chromeIntentUrl = `intent://${window.location.host}${window.location.pathname}#Intent;scheme=https;package=com.android.chrome;end`;

  // BottomNav (see useBottomNavTarget) is fixed to the bottom on mobile whenever
  // it's showing; sit above it there instead of flush at the bottom.
  const bottomOffset = bottomNavSlug
    ? 'bottom-[calc(3.25rem+env(safe-area-inset-bottom))] md:bottom-2'
    : 'bottom-2';

  return (
    <>
      <div className={`fixed inset-x-2 ${bottomOffset} z-50 flex items-center gap-3 rounded-lg border border-border bg-card p-3 shadow-xl lg:hidden`}>
        <img src="/icons/icon-192.png" alt="" className="h-10 w-10 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-foreground">Laftel Condo</p>
          <p className="text-sm text-muted-foreground">Add the app to your home screen</p>
        </div>
        {mode === 'native' ? (
          <button
            type="button"
            className="shrink-0 bg-primary text-primary-foreground px-4 py-2 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            onClick={() => install()}
          >
            Install
          </button>
        ) : (
          <button
            type="button"
            className="shrink-0 bg-primary text-primary-foreground px-4 py-2 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            onClick={() => setShowSteps(true)}
          >
            Show me how
          </button>
        )}
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground p-1 rounded"
          aria-label="Dismiss"
          onClick={dismiss}
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {showSteps && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={closeSteps}
        >
          <div
            className="bg-card border border-border rounded-lg shadow-xl w-full max-w-lg flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Install Laftel Condo</h3>
              <button
                onClick={closeSteps}
                className="text-muted-foreground hover:text-foreground p-1 rounded"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4">
              {mode === 'ios' ? (
                <ol className="list-decimal space-y-2 ps-5">
                  <li>
                    Tap the Share button <Share className="inline w-4 h-4 mx-1" aria-hidden="true" />{' '}
                    in your browser's toolbar.
                  </li>
                  <li>
                    Scroll down and tap <strong>Add to Home Screen</strong>.
                  </li>
                  <li>
                    Tap <strong>Add</strong> — the Condo app appears on your home screen.
                  </li>
                </ol>
              ) : (
                <div className="space-y-3">
                  <ol className="list-decimal space-y-2 ps-5">
                    <li>
                      Open your browser's menu <EllipsisVertical className="inline w-4 h-4 mx-1" aria-hidden="true" />
                    </li>
                    <li>
                      Tap <strong>Add app to Home screen</strong> (or <strong>Install</strong>).
                    </li>
                  </ol>
                  <p className="text-sm text-muted-foreground">
                    Prefer one tap? Chrome can install it directly:{' '}
                    <a href={chromeIntentUrl} className="text-primary underline">
                      Open in Chrome
                    </a>
                  </p>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-border flex justify-end">
              <button
                type="button"
                className="bg-primary text-primary-foreground px-4 py-2 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
                onClick={closeSteps}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default InstallBanner;
